import { queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { ApiClient } from '../api/client';
import { useApi } from './provider';

export const activityKeys = {
  workspace: (workspaceSlug: string) => ['activity', 'workspace', workspaceSlug] as const,
  project: (workspaceSlug: string, projectSlug: string) =>
    ['activity', 'project', workspaceSlug, projectSlug] as const,
  task: (workspaceSlug: string, taskUlid: string) =>
    ['activity', 'task', workspaceSlug, taskUlid] as const,
};

interface ActivityPagePayload {
  data?: unknown[];
  meta?: { next_cursor?: string | null };
}

/**
 * Cursor-paginated workspace activity feed. Returns infinite-query
 * pages so callers can render the first page and lazily fetch more
 * via the standard TanStack pattern (`fetchNextPage` + `hasNextPage`).
 *
 * Default page size 50, capped at 100 server-side. Workspace home
 * dashboard typically pulls just the first page; the dedicated
 * "Recent activity" surface (post-slice-7) can paginate further.
 */
export const useWorkspaceActivity = (
  workspaceSlug: string | undefined,
  pageSize = 50,
) => {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: activityKeys.workspace(workspaceSlug ?? ''),
    enabled: Boolean(workspaceSlug),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const query: Record<string, string> = { limit: String(pageSize) };
      if (pageParam) query.cursor = pageParam;
      const { data, error } = await api.GET('/v1/workspaces/{workspace_slug}/activity', {
        params: {
          path: { workspace_slug: workspaceSlug as string },
          query: query as never,
        },
      });
      if (error) throw error;
      return data as ActivityPagePayload;
    },
    getNextPageParam: (lastPage) => lastPage.meta?.next_cursor ?? null,
  });
};

export const projectActivityQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  projectSlug: string,
) =>
  queryOptions({
    queryKey: activityKeys.project(workspaceSlug, projectSlug),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/v1/workspaces/{workspace_slug}/projects/{projectSlug}/activity',
        {
          params: {
            path: { workspace_slug: workspaceSlug, projectSlug },
          },
        },
      );
      if (error) throw error;
      return data;
    },
  });

export const useProjectActivity = (
  workspaceSlug: string | undefined,
  projectSlug: string | undefined,
) => {
  const api = useApi();
  return useQuery({
    ...projectActivityQueryOptions(api, workspaceSlug ?? '', projectSlug ?? ''),
    enabled: Boolean(workspaceSlug && projectSlug),
  });
};

export const taskActivityQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  taskUlid: string,
) =>
  queryOptions({
    queryKey: activityKeys.task(workspaceSlug, taskUlid),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/v1/workspaces/{workspace_slug}/tasks/{taskUlid}/activity',
        {
          params: {
            path: { workspace_slug: workspaceSlug, taskUlid },
          },
        },
      );
      if (error) throw error;
      return data;
    },
  });

export const useTaskActivity = (
  workspaceSlug: string | undefined,
  taskUlid: string | undefined,
) => {
  const api = useApi();
  return useQuery({
    ...taskActivityQueryOptions(api, workspaceSlug ?? '', taskUlid ?? ''),
    enabled: Boolean(workspaceSlug && taskUlid),
  });
};
